/**
 * Hooks para gestión de clientes.
 *
 * Usa el cliente Supabase autenticado de useSupabase() para todas las operaciones.
 */
import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'
import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database } from '@/types/database'
import { useAuth0 } from '@auth0/auth0-react'
import { useSupabase } from '@/lib/supabase-context'
import { clientesService, type FiltrosClientes, type ClienteCompletoInput } from '@/services/clientesService'
import { responsablesKeys } from '@/core/hooks/useResponsables'
import { obligacionesRecurrentesService } from '@/services/obligacionesRecurrentesService'
import { PLANTILLAS_NOTIFICACION } from '@/core/types/notificaciones'
import { useNotificacionesModulo } from '@/core/hooks/useNotificacionesModulo'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'
import { useAuditor } from '@/hooks/useAuditor'
import { usePermissions } from '@/hooks/usePermissions'
import { assertPermiso } from '@/lib/permisos-guard'
import { rutaFichaCliente } from '@/lib/ruta-cliente'
import { conRefetchSoloPermitido } from '@/lib/query-permisos'
import type { Permisos } from '@/types/roles'

/**
 * Honorarios y responsables tienen permiso propio, aparte de crear o editar el cliente.
 * Cada uno se exige solo si el input trae ese campo.
 */
const assertPermisosHonorariosYResponsables = (
  puede: Partial<Permisos>,
  input: Partial<ClienteCompletoInput>
) => {
  if (input.honorarios_mensuales !== undefined) assertPermiso(puede, 'editarHonorariosCliente')
  if (input.responsables !== undefined) assertPermiso(puede, 'asignarResponsablesCliente')
}

// Query keys - camelCase unificado
export const clientesKeys = {
  all: ['clientes'] as const,
  lists: () => [...clientesKeys.all, 'list'] as const,
  list: (filtros?: FiltrosClientes) => [...clientesKeys.lists(), filtros] as const,
  details: () => [...clientesKeys.all, 'detail'] as const,
  detail: (id: string) => [...clientesKeys.details(), id] as const,
  estadoCuenta: (id: string) => [...clientesKeys.all, 'estado-cuenta', id] as const,
  servicios: (id: string) => [...clientesKeys.all, 'servicios', id] as const,
  datosNombre: (id: string) => [...clientesKeys.all, 'datos-nombre', id] as const,
}

// Hook para listar clientes con saldos
export function useClientes(filtros?: FiltrosClientes) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()
  const { esAsistente, puede, permisosListos } = usePermissions()
  const { usuario } = useOrganizacion()

  // ORR-128: el asistente solo ve los clientes donde es responsable (scoping server-side).
  // Sin verHonorariosCliente el listado no trae honorarios ni precios, y sin verMovimientos no trae el
  // saldo (ORR-613). Espera a los permisos resueltos, overrides del rol incluidos, para no pedirlos
  // antes de saberlo.
  const filtrosEfectivos: FiltrosClientes = {
    ...(esAsistente && usuario?.id ? { ...filtros, responsableUsuarioId: usuario.id } : (filtros ?? {})),
    conMontos: puede.verHonorariosCliente === true,
    conSaldo: puede.verMovimientos === true,
  }

  const permitida = permisosListos === true
  return conRefetchSoloPermitido(
    useQuery({
      queryKey: clientesKeys.list(filtrosEfectivos),
      queryFn: () => clientesService.listar(supabase!, filtrosEfectivos),
      enabled: !!supabase && !supabaseLoading && permitida,
      staleTime: 30000,
    }),
    permitida,
  )
}

// Hook para obtener un cliente por ID
export function useCliente(id: string) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()
  const { puede, permisosListos } = usePermissions()
  // Sin verHonorariosCliente la ficha no trae honorarios_mensuales. Espera a los permisos resueltos,
  // overrides del rol incluidos, para no pedirlos antes de saberlo.
  const conMontos = puede.verHonorariosCliente === true

  const permitida = permisosListos === true
  return conRefetchSoloPermitido(
    useQuery({
      queryKey: [...clientesKeys.detail(id), { conMontos }],
      queryFn: () => clientesService.obtenerPorId(supabase!, id, { conMontos }),
      enabled: !!id && !!supabase && !supabaseLoading && permitida,
      refetchOnMount: 'always',
    }),
    permitida,
  )
}

// Hook liviano para obtener solo los campos de nombre de un cliente (ORR-120).
// No trae faceta_contable/secretos; sirve para armar el nombre del archivo.
export function useClienteDatosNombre(id: string | null | undefined) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: clientesKeys.datosNombre(id ?? ''),
    queryFn: () => clientesService.obtenerDatosNombre(supabase!, id!),
    enabled: !!id && !!supabase && !supabaseLoading,
    staleTime: 5 * 60_000,
  })
}

// Hook para obtener estado de cuenta de un cliente
export function useEstadoCuenta(clienteId: string) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: clientesKeys.estadoCuenta(clienteId),
    queryFn: () => clientesService.obtenerEstadoCuenta(supabase!, clienteId),
    enabled: !!clienteId && !!supabase && !supabaseLoading,
  })
}

// Hook para obtener servicios de un cliente
export function useServiciosCliente(clienteId: string) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: clientesKeys.servicios(clienteId),
    queryFn: () => clientesService.obtenerServicios(supabase!, clienteId),
    enabled: !!clienteId && !!supabase && !supabaseLoading,
  })
}

// Hook para crear cliente (usando el metodo completo)
/**
 * ORR-270: un cliente recién dado de alta —o uno al que se le acaban de asignar impuestos— no
 * tiene por qué esperar al cron mensual para ver los vencimientos de las obligaciones recurrentes.
 *
 * Best-effort a propósito: si falla, se loguea y listo. El cliente ya está creado y el cron del
 * día 1 genera lo mismo (la RPC es idempotente); romper el alta por esto sería peor.
 *
 * Sin `assertPermiso`: el alta la puede hacer un asistente, que tiene `crearCliente` pero no
 * `editarVencimiento`. El control lo hace la propia RPC server-side (exige sesión autenticada y
 * pertenencia a la organización).
 */
async function generarRecurrentesBestEffort(
  client: SupabaseClient<Database>,
  organizacionId: string,
  clienteId: string,
  queryClient: QueryClient
): Promise<void> {
  try {
    await obligacionesRecurrentesService.generarParaCliente(client, organizacionId, clienteId)
  } catch (err) {
    console.error('[ORR-270] no se pudieron generar los vencimientos recurrentes:', err)
  } finally {
    // En `finally` a propósito: si una obligación falla después de que otra ya insertó, hubo
    // escritura igual y la caché quedaría vieja. Invalidar de más solo cuesta un refetch.
    queryClient.invalidateQueries({ queryKey: ['calendario'] })
  }
}

export function useCrearCliente() {
  const { user } = useAuth0()
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { organizacionId } = useOrganizacion()
  const { crear } = useAuditor()
  const { puede } = usePermissions()
  const { notificar, destinatariosOrg, nombreCliente } = useNotificacionesModulo()

  return useMutation({
    mutationFn: (cliente: ClienteCompletoInput) => {
      assertPermiso(puede, 'crearCliente')
      assertPermisosHonorariosYResponsables(puede, cliente)
      // El input que no trae organización toma la del contexto, así todos los callers dan el alta igual.
      const organizacion_id = cliente.organizacion_id ?? organizacionId
      if (!organizacion_id) {
        throw new Error('No hay una organización activa para dar de alta el cliente')
      }
      return clientesService.crearCompleto(supabase!, { ...cliente, organizacion_id })
    },
    onSuccess: async (data) => {
      queryClient.invalidateQueries({ queryKey: clientesKeys.lists() })
      // El mapa de responsables scopea la planilla del calendario (ORR-230 fase 4).
      queryClient.invalidateQueries({ queryKey: responsablesKeys.all })
      crear('cliente', data.id, { cliente_id: data.id })

      if (!organizacionId) return

      // ORR-270: sus vencimientos recurrentes, sin esperar al cron del día 1.
      await generarRecurrentesBestEffort(supabase!, organizacionId, data.id, queryClient)

      // Notif: nuevo cliente creado → notificar a toda la org (excluyendo
      // al actor automáticamente vía useNotificacionesModulo). Migrado del
      // patrón legacy de auto-notif al patrón canónico del Epic ORR-55.
      // Cleanup post-review (M1+M4).
      try {
        const clienteDisplay = await nombreCliente(data.id)
        const actor = user?.name || user?.email || 'Alguien'
        const destinatarios = await destinatariosOrg(organizacionId)
        if (destinatarios.length === 0) return
        const plantilla = PLANTILLAS_NOTIFICACION.clienteNuevo(clienteDisplay)
        await notificar({
          ...plantilla,
          destinatarios,
          enlace: rutaFichaCliente(data.id),
        })
        // Logueamos el actor para trazabilidad post-merge — ver audit_log.
        void actor
      } catch (err) {
        console.error('[notificaciones] error notificando nuevo cliente:', err)
      }
    },
  })
}

// Hook para actualizar cliente (usando el metodo completo)
export function useActualizarCliente() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { organizacionId } = useOrganizacion()
  const { editar } = useAuditor()
  const { puede } = usePermissions()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ClienteCompletoInput> }) => {
      assertPermiso(puede, 'editarCliente')
      assertPermisosHonorariosYResponsables(puede, data)
      return clientesService.actualizarCompleto(supabase!, id, data)
    },
    onSuccess: async (_, variables) => {
      queryClient.invalidateQueries({ queryKey: clientesKeys.lists() })
      queryClient.invalidateQueries({ queryKey: clientesKeys.detail(variables.id) })
      // El mapa de responsables scopea la planilla del calendario (ORR-230 fase 4).
      queryClient.invalidateQueries({ queryKey: responsablesKeys.all })
      editar('cliente', variables.id, { cambios: variables.data })

      // ORR-270: mismo hueco que en el alta cuando se le asigna un impuesto nuevo a un cliente
      // existente. La condición mide presencia, no cambio — el form completo manda
      // `impuestos_liquidar` siempre (Clientes.tsx:383), así que esto dispara también en ediciones
      // que no tocaron impuestos. Se acepta: la RPC acotada solo agrega lo que falta, y correrla
      // de más cuesta un round-trip idempotente, no filas de otros clientes.
      if (organizacionId && variables.data.impuestos_liquidar !== undefined) {
        await generarRecurrentesBestEffort(supabase!, organizacionId, variables.id, queryClient)
      }
    },
  })
}

// Hook para agregar UN responsable a un cliente (idempotente, sin borrar los demás).
// ORR-202: se usa al delegar una tarea a un usuario que no tiene ese cliente delegado.
export function useAgregarClienteResponsable() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { puede } = usePermissions()

  return useMutation({
    mutationFn: ({ clienteId, usuarioId }: { clienteId: string; usuarioId: string }) => {
      assertPermiso(puede, 'asignarResponsablesCliente')
      return clientesService.agregarResponsable(supabase!, clienteId, usuarioId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientesKeys.lists() })
      // El mapa de responsables scopea la planilla del calendario (ORR-230 fase 4).
      queryClient.invalidateQueries({ queryKey: responsablesKeys.all })
    },
  })
}

// Hook para asignar responsable en masa
export function useAsignarResponsablesMasivo() {
  const { user } = useAuth0()
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { puede } = usePermissions()
  const { notificar, nombreCliente } = useNotificacionesModulo()

  return useMutation({
    mutationFn: async ({ clienteIds, usuarioIds }: { clienteIds: string[]; usuarioIds: string[] }) => {
      assertPermiso(puede, 'asignarResponsablesCliente')
      let success = 0
      let errors = 0
      for (const clienteId of clienteIds) {
        try {
          await clientesService.sincronizarResponsables(supabase!, clienteId, usuarioIds)
          success++
        } catch {
          errors++
        }
      }
      return { success, errors }
    },
    onSuccess: async (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: clientesKeys.lists() })
      // La planilla del calendario scopea por este mapa: sin invalidarlo, quien acaba de
      // recibir un cliente no lo ve hasta recargar (ORR-230 fase 4).
      queryClient.invalidateQueries({ queryKey: responsablesKeys.all })

      // Notif Fase 4: cliente asignado a nuevo responsable
      // Convierte los UUIDs de usuarioIds a emails y notifica a cada uno.
      // El helper `notificar()` excluye al actor automáticamente.
      // Migrado al helper canónico en cleanup post-review (M1).
      if (!supabase || variables.usuarioIds.length === 0) return
      try {
        const { data: usuarios } = await supabase
          .from('usuarios_brainhub')
          .select('email')
          .in('id', variables.usuarioIds)
          .eq('activo', true)
        type UsuarioRow = { email: string | null }
        const emails = ((usuarios as UsuarioRow[] | null) ?? [])
          .map((u) => u.email)
          .filter((e): e is string => !!e)
        if (emails.length === 0) return

        // Si hay 1 solo cliente, obtener su nombre para un mensaje más descriptivo
        let clienteDisplay: string | null = null
        if (variables.clienteIds.length === 1) {
          const nombre = await nombreCliente(variables.clienteIds[0])
          clienteDisplay = nombre === 'Cliente sin nombre' ? null : nombre
        }

        const actor = user?.name || user?.email || 'Alguien'
        const plantilla = PLANTILLAS_NOTIFICACION.clienteAsignadoResponsable(
          variables.clienteIds.length,
          clienteDisplay,
          actor,
        )
        await notificar({
          ...plantilla,
          destinatarios: emails,
          enlace: variables.clienteIds.length === 1 ? rutaFichaCliente(variables.clienteIds[0]) : '/clientes',
        })
      } catch (err) {
        console.error('[notificaciones] error notificando asignación de responsable:', err)
      }
    },
  })
}

// Hook para eliminar cliente
export function useEliminarCliente() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { eliminar } = useAuditor()
  const { puede } = usePermissions()

  return useMutation({
    mutationFn: async (id: string) => {
      assertPermiso(puede, 'eliminarCliente')
      const cliente = await clientesService.obtenerPorId(supabase!, id)
      await clientesService.eliminar(supabase!, id)
      return {
        id,
        nombre: cliente.persona?.nombre_completo || 'Sin nombre',
        cuit: cliente.persona?.cuit || null
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: clientesKeys.lists() })
      eliminar('cliente', data.id, { nombre: data.nombre, cuit: data.cuit })
    },
  })
}
